import { AudioReactiveParameters, AnimationParameters } from "./artistWebgl";
import { Plane3D, PointVector3D } from "./geometry";
import { Box, Shape, Shape_02, Surface } from "./geometryShapes";
import { range } from "./lerp";
import { SceneParameter } from "./scenes";
import { adaptiveShapeShaderString, linesOverlay, solidColor, wireframeEbbflow, wireframeBoxes, wireframeMainBox, frequencyVisualizerTexture, wireframeCursorBox, wireframeCursorEbbflow, cursorLinesOverlay } from "./shaders";

export type ShapeInfo = {
    shapes: Array<Shape>;
    vsSource?: string;
    fsSource: string;
    displayMode?: ShapeDisplayMode;
    lineWidth?: number;
    lineZOffset?: number;
    bgShader?: string;
    overlayShader?: string;
    shapeAnimation?: (shapeAnimationParameters: AnimationParameters) => void;
}

export type ShapeDisplayMode = "solid" | "wireframe" | "overlay" | "wireframeAndOverlay";

export const generateSingleShape = (): Array<ShapeInfo> => {
    const plane = new Plane3D(new PointVector3D(0, 1, 0), new PointVector3D(0, 0));
    const shape = new Shape_02(plane, 600, 240, 550, 14, 0);
    shape.mesh = shape.mesh.subdivideTimes(4);
    //shape.mesh.twist(0, 1, -0.8, new PointVector3D(0, 1, 0));
    //shape.mesh = shape.mesh.subdivideTimes(3);
    shape.move(-150, 0.0, -550);
    shape.rotate(-0.4, 0.0, 0.1);
    //shape.initialRotation = new PointVector3D(-0.4, 0.0, 0.1);
    //shape.position = new PointVector3D(-150, 0.0, -550);
    shape.fov = 1.1;
    shape.wireframeScale = 1.005;
    shape.overlayScale = 1.03;
    return [
        {
            shapes: [shape],
            //fsSource: adaptiveShapeShaderString('perlin', {r: 35, g: 35, b: 35}, {r: 20, g: 65, b: 225}, {r: 243, g: 40, b: 55}),
            fsSource: wireframeEbbflow,
            displayMode: 'wireframeAndOverlay',
            //bgShader: adaptiveShapeShaderString('perlin', {r: 35, g: 35, b: 35}, {r: 20, g: 65, b: 225}, {r: 243, g: 40, b: 55}),
            bgShader: solidColor({r: 0, g: 0, b: 0}),
            overlayShader: linesOverlay,
            shapeAnimation: ({time, audioReactiveParameters}: AnimationParameters) => {
                //shape.rotation.x = time / 3000;
                if (audioReactiveParameters) {
                    shape.relativeClear();
                    shape.relativeUniformScale(1 + (audioReactiveParameters?.beatFade * 0.00));
                    shape.overlayScale = 1.03 + (audioReactiveParameters?.beatFade * 0.02);
                    shape.rotate(0, 0.00025 + audioReactiveParameters.energy / 600, 0);
                    //shape.rotation.y = shape.rotation.y + 0.00025 + audioReactiveParameters.energy / 600;
                } else {
                    shape.relativeClear();
                    //shape.scale.x = shape.scale.y = shape.scale.z = 1;
                    shape.overlayScale = 1.03;
                    shape.rotate(0, 0.0004, 0);
                    //shape.rotation.y = shape.rotation.y + 0.0004;
                }

                //shape.movement.z = -time * 5;
            }
        }
    ]
}

export const generateBoxes = (): Array<ShapeInfo> => {
    const cubesInCircle = 24;
    const cubeRows = 40;
    const tonnelLength = 4000;

    const plane = new Plane3D(new PointVector3D(0, 1, 0), new PointVector3D(0, 0));
    const planeZ = new Plane3D(new PointVector3D(0, 0, 1), new PointVector3D(0, 0));

    const centralBox = new Box(plane, 90, 90, 90);
    centralBox.move(0, 0, -550);
    centralBox.rotate(0, 0, 45 * (Math.PI/180));
    centralBox.fov = 0.75;
    centralBox.wireframeScale = 1.05;

    const surface = new Surface(planeZ, 500, 500);
    surface.move(0, 0, -700);
    surface.fov = 0.75;

    const boxes: Array<Box> = [];
    for (let i = 0; i < cubesInCircle * cubeRows; i ++) {
        const box = new Box(plane, 50, 50, 50);
        box.move(0, 0, -550);
        box.fov = 0.75;
        box.wireframeScale = 1.05;
        boxes.push(box);
    }


    return [
        {
            shapes: boxes,
            fsSource: wireframeBoxes,
            displayMode: 'solid',
            //bgShader: solidColor({r: 0, g: 50, b: 20}),
            shapeAnimation: ({time, anchorTime, state, audioReactiveParameters}: AnimationParameters) => {
                const stateChangeTime = time - anchorTime;
                let fadeInCoef = 1;
                let fadeOutCoef = 0;
                let scaleCoef = 1;
                let movementCoef = 0;
                if (state == 0) {
                    fadeInCoef = range(0, 3000, 0, 1, stateChangeTime);
                    scaleCoef = fadeInCoef * 2;
                }
                if (state == 2) {
                    fadeOutCoef = range(0, 3000, 0, 1, stateChangeTime);
                    movementCoef = Math.pow(fadeOutCoef, 2) * 4000;
                }
                let coef = 1;
                for (let j = 0; j < tonnelLength; j += tonnelLength/cubeRows) {
                    const currentRow = j / (tonnelLength/cubeRows);
                    let rowCoef = range(0, tonnelLength, 0, 1, j);
                    for (let i = 0; i < cubesInCircle; i++) {
                        boxes[cubesInCircle * currentRow + i].relativeClear();
                        boxes[cubesInCircle * currentRow + i].relativeRotate(0, 0, ((time/100 + (360/cubesInCircle) * i)%360 * coef + (rowCoef * 90)) * (Math.PI/180));
                        let zPos = (j + time / 10) % tonnelLength - tonnelLength;
                        //let levelCoef = levels[Math.floor(range(-tonnelLength, 0, 0, cubeRows, zPos))] * range(-tonnelLength, 0, 0, 1, zPos);
                        //cubeCircleCanvas.stroke(255 * range(-tonnelLength, 0, 0, 1, zPos));
                        boxes[cubesInCircle * currentRow + i].relativeMove(350 /*+ levelCoef*/,0,zPos + 400 + movementCoef);
                        //cubeCircleCanvas.translate(canvasSize/2 + levelCoef * canvasSize/10,0,zPos + 400);
                        boxes[cubesInCircle * currentRow + i].relativeRotate(0, 0, time/4000 * coef);
                        //cubeCircleCanvas.fill(levelCoef ** 4 * 4000);
                        boxes[cubesInCircle * currentRow + i].relativeUniformScale(Math.max(Math.min(scaleCoef - range(-tonnelLength, 0, 0, 1, zPos) * (1 - fadeInCoef), 1), 0));
                    }
                    if (coef == 1) coef = -1;
                    else coef = 1;
                }
            }
        },
        {
            shapes: [surface],
            fsSource: frequencyVisualizerTexture,
            displayMode: 'solid',
        },
        {
            shapes: [centralBox],
            fsSource: wireframeMainBox,
            displayMode: 'solid',
            //bgShader: solidColor({r: 0, g: 0, b: 0}),
            shapeAnimation: ({time, anchorTime, state, audioReactiveParameters}: AnimationParameters) => {
                const stateChangeTime = time - anchorTime;
                let fadeInCoef = 1;
                let fadeOutCoef = 0;
                let scaleCoef = 1;
                if (state == 0) {
                    fadeInCoef = range(0, 1500, 0, 1, stateChangeTime);
                    scaleCoef = 1 - Math.pow(1 - fadeInCoef, 3);
                }
                if (state == 2) {
                    fadeOutCoef = range(0, 2000, 0, 1, stateChangeTime);
                    scaleCoef = 1 - Math.pow(fadeOutCoef, 2);
                }
                if (audioReactiveParameters) {
                    centralBox.relativeClear();
                    //box.scale.x = box.scale.y = box.scale.z = 1 + (audioReactiveParameters?.beatFade * 0.02);
                    centralBox.relativeUniformScale((1 + (audioReactiveParameters?.beatFade * 0.3)) * scaleCoef);
                    centralBox.overlayScale = 1.01 + (audioReactiveParameters?.beatFade * 0.03);
                    centralBox.rotate(0,  0.00025 + audioReactiveParameters.energy / 600, 0);
                } else {
                    centralBox.relativeClear();
                    //box.scale.x = box.scale.y = box.scale.z = 1;
                    centralBox.overlayScale = 1.01;
                    //box.rotation.y = box.rotation.y + 0.0004;
                    centralBox.rotate(0.025 * (Math.PI/180),  0.0612 * (Math.PI/180), 0.01 * (Math.PI/180));
                    centralBox.relativeUniformScale(scaleCoef);
                }
                //box.movement.z = -time * 5;

            }
        }
    ]
}

export const boxCursor = (): Array<ShapeInfo> => {
    const plane = new Plane3D(new PointVector3D(0, 1, 0), new PointVector3D(0, 0, 0));
    const geometry = new Box(plane, 8, 8, 8);
    geometry.move(0, 0, -550);
    geometry.rotate(0, 0, 45 * (Math.PI/180));
    geometry.fov = 0.75;
    return [
        {
            shapes: [geometry],
            fsSource: wireframeCursorBox,
            displayMode: 'solid',
            shapeAnimation: ({}: AnimationParameters) => {
                geometry.rotate(-0.3 * (Math.PI/180),  0.24 * (Math.PI/180), -0.5 * (Math.PI/180));
            }
        }
    ]
}

export const ebbflowCursor = (): Array<ShapeInfo> => {
    const plane = new Plane3D(new PointVector3D(0, 1, 0), new PointVector3D(0, 0));
    const shape = new Shape_02(plane, 18, 4, 12, 7, 0);
    shape.mesh = shape.mesh.subdivideTimes(2);
    shape.move(0, 0, -550);
    shape.rotate(1.6, 0.0, 0.0);
    shape.fov = 1.1;
    shape.overlayScale = 1.005;
    return [
        {
            shapes: [shape],
            fsSource: solidColor({r: 0, g: 0, b: 0}),
            overlayShader: cursorLinesOverlay,
            displayMode: 'overlay',
            bgShader: solidColor({r: 0, g: 0, b: 0}),
            shapeAnimation: ({time, audioReactiveParameters}: AnimationParameters) => {
                
                shape.relativeClear();
                shape.rotate(0.014, 0.008, 0.016);
            }
        }
    ]
}